import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { TrazaColaborador } from '@/lib/supabase/tipos-bd'
import { useTrabajoDelColaborador, useTrazabilidad } from '@/modules/trazabilidad/useTrazabilidad'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { horas as enHoras, iniciales, porcentaje } from '@/shared/utilidades/formato'

import estilos from './EquipoEnCifras.module.css'

/**
 * El equipo, en el tablero.
 *
 * MEGA lo pidió así y tiene razón: estas cifras se miran todos los días, y
 * tenerlas en una sección aparte obliga a ir a buscarlas. La Sección 2 dejó de
 * existir como pantalla; vive aquí, en tarjetas.
 *
 * Cada tarjeta trae las tres cifras que se leen de un vistazo —horas,
 * puntualidad, retraso típico— y al tocarla se abren SUS órdenes debajo, una
 * por una. Una cifra sin su detalle no se puede discutir con nadie.
 *
 * SE MUESTRAN DOS CUMPLIMIENTOS Y NO SE MEZCLAN. Uno puede entregar siempre a
 * tiempo gastando el doble de horas, y otro clavar las horas entregando tarde:
 * son dos problemas distintos y se arreglan de maneras distintas.
 */

const VENTANAS = [
  { dias: 30, rotulo: '30 días' },
  { dias: 90, rotulo: '90 días' },
  { dias: 180, rotulo: '6 meses' },
  { dias: null, rotulo: 'Todo' },
] as const

export function EquipoEnCifras() {
  const [dias, setDias] = useState<number | null>(90)
  const [abierto, setAbierto] = useState<string | null>(null)
  const { colaboradores, cargando, error } = useTrazabilidad(dias)

  const conTrabajo = colaboradores.filter((c) => c.asignadas > 0)
  const sinTrabajo = colaboradores.filter((c) => c.asignadas === 0)
  const persona = conTrabajo.find((c) => c.colaborador_id === abierto) ?? null

  return (
    <>
      <div className={estilos.periodo}>
        {VENTANAS.map((ventana) => (
          <button
            key={ventana.rotulo}
            type="button"
            className={`${estilos.mando} ${dias === ventana.dias ? estilos.activo : ''}`}
            onClick={() => setDias(ventana.dias)}
          >
            {ventana.rotulo}
          </button>
        ))}
      </div>

      {error && <Aviso tipo="error">{error}</Aviso>}

      {cargando ? (
        <Cargando compacto mensaje="CALIBRANDO RENDIMIENTO TÉCNICO..." />
      ) : error ? null : conTrabajo.length === 0 ? (
        <p className={estilos.vacio}>
          Nadie tiene trabajo asignado en ese periodo. Esto se llena solo a medida que se programan
          y se ejecutan las órdenes.
        </p>
      ) : (
        <>
          <ul className={estilos.rejilla}>
            {conTrabajo.map((traza) => (
              <TarjetaColaborador
                key={traza.colaborador_id}
                traza={traza}
                abierta={abierto === traza.colaborador_id}
                onAbrir={() =>
                  setAbierto(abierto === traza.colaborador_id ? null : traza.colaborador_id)
                }
              />
            ))}
          </ul>

          {/* El detalle va debajo de la rejilla y no dentro de la tarjeta: la
              tabla tiene seis columnas y adentro de una tarjeta angosta habría
              que apretarla hasta que no se lea. */}
          {persona && <Detalle traza={persona} onCerrar={() => setAbierto(null)} />}

          {/* Al pie de la tarjeta, y en etiquetas: es el aire que sobraba
              junto a las decisiones, y un párrafo de dieciocho nombres
              seguidos no se lee. */}
          {sinTrabajo.length > 0 && (
            <div className={estilos.libres}>
              <p className={estilos.libresTitulo}>
                Sin trabajo asignado <span>{sinTrabajo.length}</span>
              </p>
              <ul className={estilos.libresLista}>
                {sinTrabajo.map((c) => (
                  <li key={c.colaborador_id}>{c.colaborador}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cien es clavado. Pasarse es que el trabajo cuesta más de lo que se cotizó;
 * quedarse corto es que se está asignando de más —también un problema, aunque
 * no duela igual—.
 */
function tonoDeHoras(valor: number | null): string {
  if (valor === null) return ''
  if (valor > 115) return estilos.malo
  if (valor < 80) return estilos.flojo
  return estilos.bueno
}

function tonoDeFecha(valor: number | null): string {
  if (valor === null) return ''
  if (valor >= 85) return estilos.bueno
  if (valor >= 60) return estilos.flojo
  return estilos.malo
}

type PropsTarjeta = { traza: TrazaColaborador; abierta: boolean; onAbrir: () => void }

function TarjetaColaborador({ traza, abierta, onAbrir }: PropsTarjeta) {
  const horas = traza.horas_ejecutadas

  return (
    <li className={`${estilos.tarjeta} ${abierta ? estilos.tarjetaAbierta : ''}`}>
      <button type="button" className={estilos.boton} onClick={onAbrir} aria-expanded={abierta}>
        <header className={estilos.encabezado}>
          <span className={estilos.avatar} aria-hidden="true">
            {iniciales(traza.colaborador)}
          </span>

          <div className={estilos.identidad}>
            <p className={estilos.nombre}>{traza.colaborador}</p>
            <p className={estilos.contexto}>
              {traza.asignadas} {traza.asignadas === 1 ? 'orden' : 'órdenes'}
              {traza.en_curso > 0 && ` · ${traza.en_curso} en curso`}
            </p>
          </div>
        </header>

        <dl className={estilos.cifras}>
          <div className={`${estilos.cifra} ${tonoDeHoras(traza.cumplimiento_horas)}`}>
            <dt>Horas</dt>
            <dd className={estilos.valor}>
              {horas}
              <span className={estilos.contra}>/ {traza.horas_asignadas}</span>
            </dd>
            <dd className={estilos.pie}>
              {traza.cumplimiento_horas === null
                ? 'sin asignar'
                : `${porcentaje(traza.cumplimiento_horas)} de lo asignado`}
            </dd>
          </div>

          <div className={`${estilos.cifra} ${tonoDeFecha(traza.cumplimiento_fecha)}`}>
            <dt>A tiempo</dt>
            <dd className={estilos.valor}>
              {traza.cumplimiento_fecha === null
                ? '—'
                : porcentaje(Math.round(traza.cumplimiento_fecha))}
            </dd>
            <dd className={estilos.pie}>
              {traza.medibles === 0
                ? 'nada terminado aún'
                : `${traza.a_tiempo} de ${traza.medibles} terminadas`}
            </dd>
          </div>

          <div className={`${estilos.cifra} ${traza.tarde > 0 ? estilos.malo : estilos.bueno}`}>
            <dt>Retraso típico</dt>
            <dd className={estilos.valor}>
              {traza.retraso_mediana_horas === null ? '—' : enHoras(traza.retraso_mediana_horas)}
            </dd>
            <dd className={estilos.pie}>
              {traza.tarde === 0
                ? 'ninguna tarde'
                : `mediana de ${traza.tarde} tarde · la peor, ${enHoras(traza.retraso_maximo_horas)}`}
            </dd>
          </div>
        </dl>

        <p className={estilos.abrir}>{abierta ? 'Cerrar' : 'Ver sus órdenes'}</p>
      </button>
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/** Las órdenes de una persona. Una cifra sin su detalle no se puede discutir. */
function Detalle({ traza, onCerrar }: { traza: TrazaColaborador; onCerrar: () => void }) {
  const navegar = useNavigate()
  const { ordenes, cargando } = useTrabajoDelColaborador(traza.colaborador_id)

  return (
    <section className={estilos.detalle}>
      <header className={estilos.detalleEncabezado}>
        <h4>Las órdenes de {traza.colaborador}</h4>
        <button type="button" className={estilos.cerrar} onClick={onCerrar}>
          Cerrar
        </button>
      </header>

      {cargando ? (
        <p className={estilos.esperando}>Cargando sus órdenes…</p>
      ) : ordenes.length === 0 ? (
        <p className={estilos.esperando}>Sin órdenes.</p>
      ) : (
        <div className="desplazable">
          <table className={estilos.tabla}>
            <thead>
              <tr>
                <th>Orden</th>
                <th>Cliente</th>
                <th>Servicio</th>
                <th className={estilos.derecha}>Asignadas</th>
                <th className={estilos.derecha}>Ejecutadas</th>
                <th className={estilos.derecha}>Retraso</th>
              </tr>
            </thead>

            <tbody>
              {ordenes.map((orden) => (
                <tr
                  key={orden.id}
                  className={estilos.fila}
                  onClick={() => navegar(`/ordenes/${orden.orden_servicio_id}`)}
                >
                  <td className={estilos.numeroOrden}>{orden.numero}</td>
                  <td>{orden.cliente_nombre ?? 'Sin cliente'}</td>
                  <td className={estilos.servicio}>
                    {orden.servicio_codigo && (
                      <span className={estilos.codigo}>{orden.servicio_codigo}</span>
                    )}
                    {orden.servicio_nombre ?? '—'}
                  </td>
                  <td className={estilos.derecha}>
                    {orden.horas_asignadas === null ? '—' : Number(orden.horas_asignadas)}
                  </td>
                  <td className={estilos.derecha}>
                    {Number(orden.horas_ejecutadas)}
                    {Number(orden.horas_extra) > 0 && (
                      <span className={estilos.extra}>+{Number(orden.horas_extra)}</span>
                    )}
                  </td>
                  <td className={estilos.derecha}>
                    {orden.retraso_horas === null ? (
                      <span className={estilos.enCurso}>en curso</span>
                    ) : Number(orden.retraso_horas) > 0 ? (
                      <span className={estilos.tarde}>
                        +{enHoras(Number(orden.retraso_horas))}
                      </span>
                    ) : (
                      <span className={estilos.puntual}>a tiempo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
